import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Group, Lead, LeadDetail, LeadPatch, Meta, NoteEntry, StageEvent, TaskInput, TaskListItem, WsEvent } from '@shared/types';
import { stageIndex } from '@shared/kpi';
import { api, ApiError, currentTenant, currentToken } from './api';

export const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false } } });

// ---- toasts --------------------------------------------------------------
type Toast = { id: number; text: string; kind: 'info' | 'error' };
const ToastCtx = createContext<{ toasts: Toast[]; push: (text: string, kind?: Toast['kind']) => void }>({ toasts: [], push: () => {} });
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 6000 : 3000);
  }, []);
  return (
    <ToastCtx.Provider value={{ toasts, push }}>
      {children}
      <div aria-live="polite">
        {toasts.map((t, i) => (
          <div key={t.id} className={`toast ${t.kind}`} style={{ bottom: `calc(${24 + i * 52}px + env(safe-area-inset-bottom))` }}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx).push;

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 503) return 'ה-CRM אינו זמין כרגע — נסה שוב בעוד רגע';
    if (e.status === 401) return 'ההתחברות פגה — רענן את הדף';
    return e.message;
  }
  return e instanceof Error ? e.message : 'שגיאה לא ידועה';
}

// ---- queries ---------------------------------------------------------------
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 5 * 60_000 });
}

export function useMeta() {
  return useQuery({ queryKey: ['meta', currentTenant()], queryFn: api.meta, staleTime: 5 * 60_000 });
}

/**
 * The tenant's leads, kept in the browser and brought up to date by what changed rather than by reloading:
 * - first load: the last 30 days (the default view), then everything older in the background;
 * - then only changes since the cursor (syncLeads) — on reconnect, when a sweep ends, every few minutes — on top of
 *   the live 'lead' / 'lead-removed' events. Never refetched as a whole except after a full rebuild ('resync').
 */
export function useLeads() {
  const qc = useQueryClient();
  return useQuery({ queryKey: ['leads', currentTenant()], queryFn: () => loadLeads(qc, currentTenant()), staleTime: Infinity });
}

const RECENT_DAYS = 30;
/** per tenant: the cursor the leads in the cache are current to */
const cursors = new Map<string | null, string>();

async function loadLeads(qc: QueryClient, tenant: string | null): Promise<Lead[]> {
  const from = new Date(Date.now() - RECENT_DAYS * 86_400_000).toISOString();
  const recent = await api.leads({ from });
  cursors.set(tenant, recent.cursor);
  void api.leads({ before: from }).then((older) => {
    if (currentTenant() !== tenant) return;
    qc.setQueryData<Lead[]>(['leads', tenant], (cur) => mergeLeads(older.leads, cur ?? recent.leads));
    void syncLeads(qc); // anything written between the two reads
  }).catch(() => { /* the recent window is shown; the next sync or reload fills the rest */ });
  return recent.leads;
}

/** `base` with `over` laid on top (by id, `over` wins), newest first. */
function mergeLeads(base: Lead[], over: Lead[]): Lead[] {
  const byId = new Map(base.map((l) => [l.id, l]));
  for (const l of over) byId.set(l.id, l);
  return [...byId.values()].sort((a, b) => (b.leadDate ?? '').localeCompare(a.leadDate ?? ''));
}

let syncing: Promise<void> | null = null;
/** Applies what changed since the cached cursor: removals first, then the changed rows. One at a time. */
export function syncLeads(qc: QueryClient): Promise<void> {
  if (syncing) return syncing;
  syncing = (async () => {
    const tenant = currentTenant();
    const since = cursors.get(tenant);
    if (!since || !qc.getQueryData<Lead[]>(['leads', tenant])) return;
    const r = await api.leadChanges(since);
    if (currentTenant() !== tenant) return;
    if (r.reset) qc.setQueryData<Lead[]>(['leads', tenant], r.leads);
    else if (r.leads.length || r.removed.length) {
      const gone = new Set(r.removed);
      qc.setQueryData<Lead[]>(['leads', tenant], (old) => mergeLeads((old ?? []).filter((l) => !gone.has(l.id)), r.leads));
    }
    cursors.set(tenant, r.cursor);
  })().catch(() => { /* next time */ }).finally(() => { syncing = null; });
  return syncing;
}

type StageEventsData = { events: StageEvent[]; since: string | null; cursor: string | null };
const eventKey = (e: StageEvent) => `${e.opportunityId}|${e.to}|${e.at}`;

/** Recorded status changes, for the funnel on /stats: all once, then only those recorded since (the history only grows). */
export function useStageEvents() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['stage-events', currentTenant()],
    queryFn: async ({ queryKey }): Promise<StageEventsData> => {
      const prev = qc.getQueryData<StageEventsData>(queryKey);
      const r = await api.stageEvents(prev?.cursor ?? undefined);
      if (!prev?.cursor) return r;
      const seen = new Set(prev.events.map(eventKey));
      const added = r.events.filter((e) => !seen.has(eventKey(e)));
      const events = added.length ? [...prev.events, ...added].sort((a, b) => a.at.localeCompare(b.at)) : prev.events;
      return { events, since: r.since, cursor: r.cursor ?? prev.cursor };
    },
    staleTime: 60_000,
    refetchInterval: 300_000,
  });
}

export function useLeadDetail(id: string | null) {
  return useQuery({ queryKey: ['lead', id], queryFn: () => api.lead(id!), enabled: !!id, staleTime: 30_000 });
}

/** Stage lookup helpers derived from meta. */
export function useStages() {
  const { data: meta } = useMeta();
  return useMemo(() => {
    const idx = stageIndex(meta?.pipelines ?? []);
    const name = (id: string) => idx.get(id)?.name ?? '—';
    const users = new Map((meta?.users ?? []).map((u) => [u.id, u.name]));
    const userName = (id: string | null) => (id ? users.get(id) ?? '—' : '—');
    return { idx, stageName: name, userName, meta };
  }, [meta]);
}

const leadsKey = () => ['leads', currentTenant()] as const;
const metaKey = () => ['meta', currentTenant()] as const;

function upsertLeadInCache(qc: QueryClient, lead: Lead) {
  qc.setQueryData<Lead[]>(leadsKey(), (old) => {
    if (!old) return old;
    const i = old.findIndex((l) => l.id === lead.id);
    if (i === -1) return [lead, ...old];
    const next = old.slice();
    next[i] = lead;
    return next;
  });
  qc.setQueryData<LeadDetail>(['lead', lead.id], (old) => (old ? { ...old, ...lead } : old));
}

// ---- live updates ------------------------------------------------------------
export function useLiveUpdates() {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const retry = useRef(1000);
  useEffect(() => {
    let ws: WebSocket | null = null;
    let closed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const connect = async () => {
      const token = await currentToken();
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const qs = new URLSearchParams();
      if (token) qs.set('token', token);
      if (currentTenant()) qs.set('tenant', currentTenant()!);
      ws = new WebSocket(`${proto}://${location.host}/api/ws?${qs}`);
      // (re)connected: catch up on whatever happened while the socket was down
      ws.onopen = () => { setConnected(true); retry.current = 1000; void syncLeads(qc); };
      ws.onmessage = (ev) => {
        if (ev.data === 'pong') return;
        let events: WsEvent[] = [];
        try { events = JSON.parse(ev.data); } catch { return; }
        for (const e of events) {
          if (e.type === 'lead') upsertLeadInCache(qc, e.lead);
          else if (e.type === 'resync') void qc.invalidateQueries({ queryKey: ['leads'] }); // a full rebuild: reload the list
          else if (e.type === 'lead-removed') qc.setQueryData<Lead[]>(leadsKey(), (old) => old?.filter((l) => l.id !== e.id));
          else if (e.type === 'tasks' && !qc.isMutating({ mutationKey: ['task-write'] })) void qc.invalidateQueries({ queryKey: ['tasks', currentTenant()] });
          else if (e.type === 'groups') qc.setQueryData<Meta>(metaKey(), (old) => (old ? { ...old, groups: e.groups } : old));
          else if (e.type === 'sync') {
            qc.setQueryData<Meta>(metaKey(), (old) => (old ? { ...old, sync: e.status } : old));
            // a sweep ended: its changes came as 'lead' events; a sync by cursor covers any this screen missed
            if (!e.status.running) { void syncLeads(qc); void qc.invalidateQueries({ queryKey: ['meta'] }); }
          }
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closed) return;
        timer = setTimeout(connect, retry.current);
        retry.current = Math.min(retry.current * 2, 30_000);
      };
      ws.onerror = () => ws?.close();
    };
    void connect();
    const ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 30_000);
    // a safety net for anything the socket missed: cheap (an index range that is usually empty)
    const catchUp = setInterval(() => { if (document.visibilityState === 'visible') void syncLeads(qc); }, 5 * 60_000);
    return () => { closed = true; clearInterval(ping); clearInterval(catchUp); if (timer) clearTimeout(timer); ws?.close(); };
  }, [qc]);
  return connected;
}

// ---- mutations (optimistic, reconciled against GHL's response) -----------------
function useOptimisticLead<TVars extends { id: string }>(apply: (lead: Lead, vars: TVars) => Lead, call: (vars: TVars) => Promise<Lead>, failText: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: call,
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: leadsKey() });
      const prev = qc.getQueryData<Lead[]>(leadsKey());
      const prevDetail = qc.getQueryData<LeadDetail>(['lead', vars.id]);
      const cur = prev?.find((l) => l.id === vars.id);
      if (cur) upsertLeadInCache(qc, apply(cur, vars));
      return { prev, prevDetail };
    },
    onError: (e, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(leadsKey(), ctx.prev);
      if (ctx?.prevDetail) qc.setQueryData(['lead', vars.id], ctx.prevDetail);
      toast(`${failText}: ${errorText(e)}`, 'error');
    },
    onSuccess: (lead) => upsertLeadInCache(qc, lead),
  });
}

export function useSetStage() {
  return useOptimisticLead<{ id: string; stageId: string }>((l, v) => ({ ...l, stageId: v.stageId }), (v) => api.setStage(v.id, v.stageId), 'שינוי הסטטוס נכשל');
}
export function useSetOwner() {
  return useOptimisticLead<{ id: string; assignedTo: string | null }>((l, v) => ({ ...l, assignedTo: v.assignedTo }), (v) => api.setOwner(v.id, v.assignedTo), 'שינוי האחראי נכשל');
}
export function usePatchLead() {
  return useOptimisticLead<{ id: string; patch: LeadPatch }>(
    (l, v) => ({ ...l, ...(v.patch.fullName !== undefined ? { fullName: v.patch.fullName } : {}), ...(v.patch.phone !== undefined ? { phone: v.patch.phone } : {}), ...(v.patch.email !== undefined ? { email: v.patch.email } : {}), ...(v.patch.source !== undefined ? { source: v.patch.source } : {}) }),
    (v) => api.patchLead(v.id, v.patch),
    'שמירת הליד נכשלה',
  );
}

/**
 * Optimistic notes: a note (or an edit) shows at once in the lead's timeline, count and last-note preview, as if the
 * CRM had accepted it; only an error puts everything back (and says why). The server's answer replaces the timeline,
 * keeping notes of this lead that are still on their way.
 */
type NoteSnapshot = { detail: LeadDetail | undefined; leads: Lead[] | undefined; pendingId?: string };
const isPendingNote = (n: NoteEntry) => n.id.startsWith('pending_');

function revertNotes(qc: ReturnType<typeof useQueryClient>, leadId: string, snap: NoteSnapshot | undefined) {
  if (!snap) return;
  // put back only what this write changed: other pending notes of the lead stay
  qc.setQueryData<LeadDetail>(['lead', leadId], (old) => {
    if (!old || !snap.detail) return old;
    const others = old.notes.filter((n) => isPendingNote(n) && n.id !== snap.pendingId && !snap.detail!.notes.some((x) => x.id === n.id));
    return { ...snap.detail, notes: [...snap.detail.notes, ...others], noteCount: snap.detail.noteCount + others.length };
  });
  if (snap.leads) qc.setQueryData(leadsKey(), snap.leads);
}

function confirmNotes(qc: ReturnType<typeof useQueryClient>, leadId: string, notes: NoteEntry[], pendingId?: string) {
  qc.setQueryData<LeadDetail>(['lead', leadId], (old) => {
    if (!old) return old;
    const stillPending = old.notes.filter((n) => isPendingNote(n) && n.id !== pendingId);
    return { ...old, notes: [...notes, ...stillPending] };
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (v: { id: string; text: string }) => api.addNote(v.id, v.text),
    onMutate: async (v): Promise<NoteSnapshot> => {
      await qc.cancelQueries({ queryKey: ['lead', v.id] }); // an in-flight read must not overwrite the new note
      const meta = qc.getQueryData<Meta>(metaKey());
      const snap: NoteSnapshot = { detail: qc.getQueryData<LeadDetail>(['lead', v.id]), leads: qc.getQueryData<Lead[]>(leadsKey()), pendingId: `pending_${Date.now()}` };
      const note: NoteEntry = { id: snap.pendingId!, body: v.text, authorUserId: meta?.me.ghlUserId ?? null, createdAt: new Date().toISOString(), kind: 'comment' };
      qc.setQueryData<LeadDetail>(['lead', v.id], (old) => (old ? { ...old, notes: [...old.notes, note], noteCount: old.noteCount + 1, lastNote: v.text } : old));
      qc.setQueryData<Lead[]>(leadsKey(), (old) => old?.map((l) => (l.id === v.id ? { ...l, noteCount: l.noteCount + 1, lastNote: v.text } : l)));
      return snap;
    },
    onError: (e, v, snap) => { revertNotes(qc, v.id, snap); toast(`הוספת ההערה נכשלה: ${errorText(e)}`, 'error'); },
    onSuccess: (notes, v, snap) => confirmNotes(qc, v.id, notes, snap?.pendingId),
  });
}

/** Edits an internal comment (own, or any for an admin); the CRM gets a correction comment. Count unchanged. */
export function useEditNote() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (v: { id: string; noteId: string; text: string }) => api.editNote(v.id, v.noteId, v.text),
    onMutate: async (v): Promise<NoteSnapshot> => {
      await qc.cancelQueries({ queryKey: ['lead', v.id] });
      const meta = qc.getQueryData<Meta>(metaKey());
      const snap: NoteSnapshot = { detail: qc.getQueryData<LeadDetail>(['lead', v.id]), leads: qc.getQueryData<Lead[]>(leadsKey()) };
      const edited = { at: new Date().toISOString(), by: meta?.me.ghlUserId ?? null };
      qc.setQueryData<LeadDetail>(['lead', v.id], (old) => {
        if (!old) return old;
        const notes = old.notes.map((n) => (n.id === v.noteId ? { ...n, body: v.text, edited } : n));
        // the last-note preview follows when the edited note is the latest comment
        const last = [...notes].reverse().find((n) => n.kind === 'comment');
        return { ...old, notes, lastNote: last?.id === v.noteId ? v.text : old.lastNote };
      });
      const lastNote = qc.getQueryData<LeadDetail>(['lead', v.id])?.lastNote;
      if (lastNote !== undefined) qc.setQueryData<Lead[]>(leadsKey(), (old) => old?.map((l) => (l.id === v.id ? { ...l, lastNote } : l)));
      return snap;
    },
    onError: (e, v, snap) => { revertNotes(qc, v.id, snap); toast(`עריכת ההערה נכשלה: ${errorText(e)}`, 'error'); },
    onSuccess: (notes, v) => confirmNotes(qc, v.id, notes),
  });
}

// ---- tasks: a view over the CRM's contact tasks ------------------------------
/** ['tasks', tenant, ...]: the whole family is invalidated when any contact's tasks change (a 'tasks' event). */
export function useLeadTasks(id: string | null) {
  return useQuery({ queryKey: ['tasks', currentTenant(), 'lead', id], queryFn: () => api.leadTasks(id!), enabled: !!id, staleTime: 30_000 });
}
export function useAllTasks(completed: boolean) {
  return useQuery({ queryKey: ['tasks', currentTenant(), 'all', completed], queryFn: () => api.tasks(completed), staleTime: 30_000 });
}
/** Moves the lead's open-task count on the list and in its detail; the server's 'lead' event carries the real one. */
function bumpTaskCount(qc: ReturnType<typeof useQueryClient>, leadId: string, by: number) {
  if (!by) return;
  qc.setQueryData<Lead[]>(leadsKey(), (old) => old?.map((l) => (l.id === leadId ? { ...l, openTaskCount: Math.max(0, l.openTaskCount + by) } : l)));
  qc.setQueryData<LeadDetail>(['lead', leadId], (old) => (old ? { ...old, openTaskCount: Math.max(0, old.openTaskCount + by) } : old));
}

/**
 * Optimistic task writes: the change shows at once in every task list (the lead's tab, the tasks page) and in the
 * lead's open-task count, as if the CRM had accepted it. Only an error puts everything back (and says why). The lists
 * are re-read from the server once the last pending task write settles, so overlapping writes never flicker.
 */
type TaskSnapshot = { lists: Array<[readonly unknown[], TaskListItem[] | undefined]>; leads: Lead[] | undefined; detail: LeadDetail | undefined; leadId: string };
const TASK_WRITE = ['task-write'] as const;
const tasksKey = () => ['tasks', currentTenant()] as const;

async function applyTaskChange(qc: ReturnType<typeof useQueryClient>, leadId: string, patch: (list: TaskListItem[], key: readonly unknown[]) => TaskListItem[], countBy: number): Promise<TaskSnapshot> {
  await qc.cancelQueries({ queryKey: tasksKey() }); // an in-flight read must not overwrite the change
  const snap: TaskSnapshot = {
    lists: qc.getQueriesData<TaskListItem[]>({ queryKey: tasksKey() }),
    leads: qc.getQueryData<Lead[]>(leadsKey()),
    detail: qc.getQueryData<LeadDetail>(['lead', leadId]),
    leadId,
  };
  for (const [key, data] of snap.lists) if (data) qc.setQueryData(key, patch(data, key));
  bumpTaskCount(qc, leadId, countBy);
  return snap;
}

function revertTaskChange(qc: ReturnType<typeof useQueryClient>, snap: TaskSnapshot | undefined) {
  if (!snap) return;
  for (const [key, data] of snap.lists) qc.setQueryData(key, data);
  if (snap.leads) qc.setQueryData(leadsKey(), snap.leads);
  if (snap.detail) qc.setQueryData(['lead', snap.leadId], snap.detail);
}

function settleTaskWrite(qc: ReturnType<typeof useQueryClient>) {
  // this mutation still counts as pending here: re-read only when it is the last one
  if (qc.isMutating({ mutationKey: TASK_WRITE }) <= 1) void qc.invalidateQueries({ queryKey: tasksKey() });
}

export function useAddTask() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationKey: TASK_WRITE,
    mutationFn: (v: { id: string; task: TaskInput }) => api.addTask(v.id, v.task),
    onMutate: (v) => {
      const lead = qc.getQueryData<Lead[]>(leadsKey())?.find((l) => l.id === v.id);
      // a stand-in row until the CRM's id arrives (its actions wait for that)
      const row: TaskListItem = {
        id: `pending_${Date.now()}`, contactId: v.id, title: v.task.title, body: v.task.body ?? null, assignedTo: v.task.assignedTo ?? null,
        dueDate: v.task.dueDate, completed: false, leadName: lead?.fullName ?? null, isLead: true,
      };
      // into this lead's list and the tasks page lists (['tasks', tenant, 'lead', id] / ['tasks', tenant, 'all', …])
      return applyTaskChange(qc, v.id, (list, key) => (key[2] === 'lead' && key[3] !== v.id ? list : [...list, row]), 1);
    },
    onError: (e, _v, snap) => { revertTaskChange(qc, snap); toast(`הוספת המשימה נכשלה: ${errorText(e)}`, 'error'); },
    onSettled: () => settleTaskWrite(qc),
  });
}
export function useUpdateTask() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationKey: TASK_WRITE,
    mutationFn: (v: { taskId: string; leadId: string; task: TaskInput }) => api.updateTask(v.taskId, v.task),
    onMutate: (v) => applyTaskChange(qc, v.leadId, (list) => list.map((t) => (t.id === v.taskId
      ? { ...t, title: v.task.title, body: v.task.body ?? null, dueDate: v.task.dueDate, assignedTo: v.task.assignedTo ?? t.assignedTo }
      : t)), 0),
    onError: (e, _v, snap) => { revertTaskChange(qc, snap); toast(`עדכון המשימה נכשל: ${errorText(e)}`, 'error'); },
    onSettled: () => settleTaskWrite(qc),
  });
}
export function useDeleteTask() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationKey: TASK_WRITE,
    mutationFn: (v: { taskId: string; leadId: string; open: boolean }) => api.deleteTask(v.taskId),
    onMutate: (v) => applyTaskChange(qc, v.leadId, (list) => list.filter((t) => t.id !== v.taskId), v.open ? -1 : 0),
    onError: (e, _v, snap) => { revertTaskChange(qc, snap); toast(`מחיקת המשימה נכשלה: ${errorText(e)}`, 'error'); },
    onSettled: () => settleTaskWrite(qc),
  });
}
export function useCompleteTask() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationKey: TASK_WRITE,
    mutationFn: (v: { taskId: string; leadId: string; completed: boolean }) => api.completeTask(v.taskId, v.completed),
    onMutate: (v) => applyTaskChange(qc, v.leadId, (list) => list.map((t) => (t.id === v.taskId ? { ...t, completed: v.completed } : t)), v.completed ? -1 : 1),
    onError: (e, v, snap) => { revertTaskChange(qc, snap); toast(`${v.completed ? 'סימון המשימה כבוצעה' : 'פתיחת המשימה מחדש'} נכשל: ${errorText(e)}`, 'error'); },
    onSettled: () => settleTaskWrite(qc),
  });
}

export function useBulk() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (v: { ids: string[]; stageName?: string; assignedTo?: string | null }) => api.bulk(v.ids, { stageName: v.stageName, assignedTo: v.assignedTo }),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      toast(failed.length ? `${r.results.length - failed.length} עודכנו, ${failed.length} נכשלו` : `${r.results.length} לידים עודכנו`, failed.length ? 'error' : 'info');
      void syncLeads(qc);
    },
    onError: (e) => toast(`הפעולה נכשלה: ${errorText(e)}`, 'error'),
  });
}

export function useSaveGroups() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (groups: Group[]) => api.saveGroups(groups),
    onSuccess: (groups) => { qc.setQueryData<Meta>(metaKey(), (old) => (old ? { ...old, groups } : old)); toast('הקבוצות נשמרו'); },
    onError: (e) => toast(`שמירת הקבוצות נכשלה: ${errorText(e)}`, 'error'),
  });
}
